import cx from "classnames"

export interface SegmentedOption<T extends string> {
    id: T
    label: string
    hint?: string
}

/**
 * A square segmented control. The selected segment lifts to the surface
 * color and the rest sit flush, so the eye reads one choice, not four buttons.
 */
export function Segmented<T extends string>({
    options,
    value,
    onChange,
    label,
    compact = false,
}: {
    options: readonly SegmentedOption<T>[]
    value: T
    onChange(value: T): void
    label: string
    compact?: boolean
}) {
    const active = options.find((option) => option.id === value)
    return (
        <div className="flex flex-col gap-2">
            <div
                role="radiogroup"
                aria-label={label}
                className="grid border border-border bg-base-200 p-1"
                style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
            >
                {options.map((option) => {
                    const selected = option.id === value
                    return (
                        <button
                            type="button"
                            key={option.id}
                            role="radio"
                            aria-checked={selected}
                            className={cx(
                                "btn flex items-center justify-center text-center font-medium transition-[background-color,color,box-shadow] duration-150",
                                compact ? "min-h-8 px-2 text-xs" : "min-h-10 px-3 text-sm",
                                selected
                                    ? "border border-border bg-base-100 text-base-content shadow-sm"
                                    : "border border-transparent text-muted hover:text-base-content"
                            )}
                            onClick={() => onChange(option.id)}
                        >
                            {option.label}
                        </button>
                    )
                })}
            </div>
            {active?.hint && (
                <p className="min-h-5 text-xs leading-5 text-muted" aria-live="polite">
                    {active.hint}
                </p>
            )}
        </div>
    )
}
